import React, { useEffect, useRef, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormArea as FormAreaStyle, FormAreaCompact, FormRow, FormRowCompact, FormRowTight, FormField, FormFieldLabel } from 'styles/common.styles';
import { TextField, TextAreaField, SelectField, MultiSelectField, RadioField, CheckboxField, DateField, DateRangeField, FileField, ButtonField, NumberField, FiscalYearField } from 'components/forms/elements';
import { showAlert } from 'utils/toastBridge';
import { theme } from 'styles/theme';

/**
 * Form 영역 공통 컴포넌트 (react-hook-form 버전)
 * 
 * @param {Object} props
 * @param {Array} props.fields - 필드 정의 배열 
 * @param {string} props.fieldWidth - 필드 너비
 * @param {Object} props.extraProps - 추가 props (각 필드에 전달될 추가 속성)
 * @param {Function} props.onFormChange - form 필드 변경 시 호출될 콜백 함수
 *                                    (formData, prevFormData, changedFieldName) => void
 * @param {Object} props.defaultValues - form의 기본값 (onFormChange에서 이전 값 비교용)
 * @param {Function} props.onFieldChange - 특정 필드의 특정 이벤트 발생 시 호출될 콜백 함수
 *                                    (fieldName, value, fieldType, eventType) => void
 *                                    - fieldName: 필드 이름
 *                                    - value: 필드 값
 *                                    - fieldType: 필드 타입 ('text', 'select', 'date', 'dateRange' 등)
 *                                    - eventType: 이벤트 타입 ('enter', 'change', 'select' 등)
 * @param {boolean} props.compact - 조회조건 한 줄 밀집 (줄바꿈 없음)
 * @param {boolean} props.dense - 상세 폼 간격 축소 (줄바꿈 허용)
 */
const FormArea = ({
  fields = [],
  fieldWidth,
  extraProps = {},
  onFormChange,
  defaultValues = {},
  onFieldEnter,
  compact = false,
  dense = false,
}) => {
  const { watch, setValue, register, getValues } = useFormContext();

  // 모든 필드를 평탄화하여 required 필드들과 pattern 검증을 register
  React.useEffect(() => {
    const allFields = fields.flat();
    allFields.forEach((field) => {
      // 버튼·표시전용(plain) 필드는 validation 제외
      if (field.type === 'button' || field.type === 'plain') return;
      
      // validation 규칙 객체
      const validationRules = {};
      
      // required 검증
      if (field.required) {
        if (field.type === 'checkbox') {
          // checkbox 검증: options가 없거나 빈 배열이면 단일 체크박스, 있으면 다중 체크박스
          const isSingle = !field.options || field.options.length === 0;
          if (isSingle) {
            // 단일 체크박스: "Y"여야 함 (체크되어 있어야 함)
            validationRules.required = "["+ field.label+ "] 은(는) 필수 항목입니다";
            validationRules.validate = (value) => {
              if (value !== 'Y') {
                return "["+ field.label+ "] 은(는) 필수 항목입니다";
              }
              return true;
            };
          } else {
            // 다중 체크박스: 문자열이 비어있지 않아야 함
            const separator = field.separator || ',';
            validationRules.required = "["+ field.label+ "] 은(는) 최소 하나 이상 선택해주세요";
            validationRules.validate = (value) => {
              // 문자열을 배열로 변환하여 체크
              const valueArray = value ? value.split(separator).filter(v => v) : [];
              if (!value || valueArray.length === 0) {
                return "["+ field.label+ "] 은(는) 최소 하나 이상 선택해주세요";
              }
              return true;
            };
          }
        } else if (field.type === 'multiSelect') {
          // multiSelect: 문자열이 비어있지 않아야 함
          const separator = field.separator || ',';
          validationRules.required = "["+ field.label+ "] 은(는) 최소 하나 이상 선택해주세요";
          validationRules.validate = (value) => {
            // 문자열을 배열로 변환하여 체크
            const valueArray = value ? value.split(separator).filter(v => v) : [];
            if (!value || valueArray.length === 0) {
              return "["+ field.label+ "] 은(는) 최소 하나 이상 선택해주세요";
            }
            return true;
          };
        } else {
          // text, select 등은 값이 있어야 함
          validationRules.required = "["+ field.label+ "] 은(는) 필수 항목입니다";
        }
      }
      
      // pattern 검증 (text 타입에만 적용)
      if (field.type === 'text' && field.pattern) {
        validationRules.pattern = {
          value: field.pattern instanceof RegExp ? field.pattern : new RegExp(field.pattern),
          message: field.patternMessage || `[${field.label}] 형식이 올바르지 않습니다.`,
        };
      }
      
      // number 타입의 min/max 검증
      if (field.type === 'number') {
        if (field.min !== undefined && field.min !== null) {
          validationRules.min = {
            value: field.min,
            message: `[${field.label}] 최소값은 ${field.min}입니다.`,
          };
        }
        if (field.max !== undefined && field.max !== null) {
          validationRules.max = {
            value: field.max,
            message: `[${field.label}] 최대값은 ${field.max}입니다.`,
          };
        }
      }
      
      // validation 규칙이 있으면 register
      if (Object.keys(validationRules).length > 0) {
        register(field.name, validationRules);
      }
    });
  }, [fields, register]);

  // ==================== Form 변경 감시 및 공통 함수 호출 ====================
  // 이전 form 데이터를 저장하기 위한 ref (초기값은 defaultValues)
  const prevFormDataRef = useRef({ ...defaultValues });
  const isFirstRender = useRef(true); // 첫 렌더링 여부를 추적

  // onFormChange를 호출하는 헬퍼 함수
  // setValue 직후에 호출되므로, 약간의 지연을 두고 현재 폼 데이터를 가져옴
  const triggerFormChange = useCallback((fieldName, prevValue, newValue) => {
    if (!onFormChange || isFirstRender.current) {
      return;
    }
    
    // triggerFormChange가 호출된 필드를 추적 (중복 호출 방지)
    triggerFormChangeFieldsRef.current.add(fieldName);
    
    // setValue 후 약간의 지연을 두고 현재 폼 데이터 가져오기
    setTimeout(() => {
      const prevFormData = prevFormDataRef.current;
      const currentFormData = getValues();
      
      // 이전 폼 데이터를 복사하고 현재 필드의 이전 값으로 업데이트
      const updatedPrevFormData = { ...prevFormData };
      if (prevValue !== undefined) {
        updatedPrevFormData[fieldName] = prevValue;
      }
      
      // 실제로 값이 변경되었는지 확인
      // 특정 필드의 값이 변경되었는지 확인
      const fieldChanged = currentFormData[fieldName] !== updatedPrevFormData[fieldName];
      
      if (fieldChanged) {
        // 값이 실제로 변경된 경우에만 콜백 호출
        onFormChange(currentFormData, updatedPrevFormData, fieldName);
        // 현재 값을 이전 값으로 저장 (다음 변경 시 사용)
        prevFormDataRef.current = { ...currentFormData };
      }
      
      // 처리 완료 후 추적에서 제거
      setTimeout(() => {
        triggerFormChangeFieldsRef.current.delete(fieldName);
      }, 10);
    }, 0);
  }, [onFormChange, getValues]);

  // triggerFormChange가 호출된 필드를 추적하기 위한 ref (중복 호출 방지)
  const triggerFormChangeFieldsRef = useRef(new Set());

  // form 데이터 변경 감시 (모든 필드 변경 시 onFormChange 호출)
  // 외부에서 methods.setValue로 직접 변경한 경우도 감지
  useEffect(() => {
    // onFormChange가 제공되지 않았으면 감시하지 않음
    if (!onFormChange) {
      return;
    }

    const subscription = watch((formData, info) => {
      // 첫 렌더링 시에는 실행하지 않음
      if (isFirstRender.current) {
        // 현재 값을 이전 값으로 저장하고 다음부터 감지 시작
        prevFormDataRef.current = { ...formData };
        isFirstRender.current = false;
        return;
      }

      // info가 객체인 경우 name과 type 추출, 없으면 null
      const changedFieldName = info?.name || null;
      const prevFormData = prevFormDataRef.current;
      
      // triggerFormChange가 이미 호출된 필드는 스킵 (중복 호출 방지)
      // 단, 외부에서 setValue로 직접 변경한 경우는 감지해야 하므로
      // triggerFormChangeFieldsRef에 있더라도 일정 시간 후에는 처리
      if (changedFieldName && triggerFormChangeFieldsRef.current.has(changedFieldName)) {
        // triggerFormChange가 호출된 필드는 약간의 지연 후에 처리
        // (triggerFormChange의 setTimeout과 겹치지 않도록)
        setTimeout(() => {
          triggerFormChangeFieldsRef.current.delete(changedFieldName);
        }, 10);
        return;
      }
      
      // 실제로 값이 변경되었는지 확인 (JSON 문자열 비교)
      const currentStr = JSON.stringify(formData);
      const prevStr = JSON.stringify(prevFormData);
      
      if (currentStr !== prevStr) {
        // 값이 실제로 변경된 경우에만 콜백 호출
        // 약간의 지연을 두어 setValue 직후의 변경도 확실히 감지
        setTimeout(() => {
          const currentFormData = getValues();
          const finalPrevFormData = prevFormDataRef.current;
          const finalCurrentStr = JSON.stringify(currentFormData);
          const finalPrevStr = JSON.stringify(finalPrevFormData);
          
          if (finalCurrentStr !== finalPrevStr) {
            onFormChange(currentFormData, finalPrevFormData, changedFieldName);
            // 현재 값을 이전 값으로 저장 (다음 변경 시 사용)
            prevFormDataRef.current = { ...currentFormData };
          }
        }, 0);
      }
    });
    
    // cleanup 함수: 컴포넌트 언마운트 시 구독 해제
    return () => {
      subscription.unsubscribe();
    };
  }, [watch, onFormChange, getValues]);

  /**
   * 단일 필드 렌더링
   */
  const renderField = (field, onFieldEnterParam, triggerFormChangeParam, onFormChangeParam) => {
    // 파라미터로 전달받은 함수들을 사용 (클로저 문제 방지)
    const fieldOnFieldEnter = onFieldEnterParam || onFieldEnter;
    const fieldOnFormChange = onFormChangeParam || onFormChange;
    const fieldTriggerFormChange = triggerFormChangeParam || triggerFormChange;
    const {
      name,
      label,
      type = 'text',
      width,
      flex,
      options = [],
      onChange: fieldOnChange,
      onKeyDown,
      onKeyUp,
      readonly = false,
      labelWidth,
      parentWidth,
      min,
      max,
      step, // number 타입에서 사용할 증가/감소 단위
      rows,
      startLabel,
      endLabel,
      startFieldName,
      endFieldName,
      required = false,
      showColon = false,
      requiredMarkPosition = 'prefix',
      separator = ',', // 배열을 문자열로 변환할 때 사용할 구분자 (기본값: ',')
      pattern, // 정규식 패턴 (text 타입에서 사용)
      patternMessage, // pattern 검증 실패 시 표시될 메시지 (선택)
      roundMode, // number 타입에서 사용할 반올림 모드: 'round' (반올림), 'floor' (버림), 'ceil' (올림)
      // 버튼 필드 관련 props
      buttonLabel, // 버튼에 표시될 텍스트 (label과 별개)
      icon, // 버튼 아이콘
      onClick, // 버튼 클릭 핸들러
      variant, // 버튼 스타일 ('primary', 'success', 'danger' 또는 없음)
      showSearch = false, // text 옆 돋보기(코드조회)
      onSearch, // 돋보기 클릭 핸들러
      searchTitle,
      showTodayShortcut = true,
      showPresets, // date: 기본 false / dateRange: 기본 true (아래 resolved)
      presetSet = 'inquiry', // 'inquiry' | 'minimal'
      fiscalYear, // 분기·반기·~오늘 연도 기준
      anchorFrom, // 「~ 오늘」시작일
      yearPreset = 'calendar', // 'rolling' | 'calendar' — 1년 프리셋 기본: 당해 1/1~12/31
      pairWith, // M3: 시작일 필드 → 종료일 필드명
      pairStartField, // M3: 종료일 필드 → 시작일 필드명
      allowEmptyEnd = true,
      ...fieldProps
    } = field;

    // 단일(date): 프리셋 기본 off / 기간(dateRange): 프리셋 기본 on
    const resolvedShowPresets =
      showPresets !== undefined ? showPresets : type === 'dateRange';

    const fieldExtraProps = extraProps[name] || {};
    //const fieldError = errors[name];

    // 라벨의 필수 표시(*) 위치를 필드 단위로 제어 (기본: prefix → *컬럼명)
    const displayLabel = required
      ? (requiredMarkPosition === 'suffix' ? `${label} *` : `*${label}`)
      : label;

    // 공통 props
    const commonProps = {
      name,
      label: displayLabel,
      showColon,
      width,
      flex,
      labelWidth,
      fieldWidth: parentWidth || fieldWidth,
      readonly,
      fieldProps: { ...fieldProps, ...fieldExtraProps },
    };

    // 버튼 필드 타입 처리
    if (type === 'button') {
      return (
        <ButtonField
          key={name}
          name={name}
          label={label} // 라벨은 선택사항 (없으면 버튼만 표시)
          buttonLabel={buttonLabel} // 버튼에 표시될 텍스트
          icon={icon}
          onClick={onClick}
          variant={variant}
          disabled={readonly}
          width={width}
          flex={flex}
          labelWidth={labelWidth}
          fieldWidth={parentWidth || fieldWidth}
          fieldProps={fieldExtraProps}
        />
      );
    }

    // 표시 전용 텍스트 (입력 박스 없음 — 상태 문구 등)
    if (type === 'plain') {
      const value = watch(name) || '';
      const {
        style: plainStyle = {},
        title: plainTitle,
        ...restPlainProps
      } = fieldProps;
      const { marginLeft, marginRight, marginTop, marginBottom, ...textStyle } = plainStyle;
      return (
        <FormField
          key={name}
          flex={flex || '0 0 auto'}
          width={parentWidth || fieldWidth || 'auto'}
          style={{
            marginLeft: marginLeft ?? '-8px',
            marginRight,
            marginTop,
            marginBottom,
          }}
        >
          {label ? (
            <FormFieldLabel width={labelWidth}>
              {showColon ? `${label}:` : label}
            </FormFieldLabel>
          ) : null}
          <span
            title={plainTitle}
            style={{
              display: 'inline-block',
              fontSize: theme.typography.fontSize.sm,
              lineHeight: 1.3,
              whiteSpace: 'nowrap',
              ...textStyle,
            }}
            {...restPlainProps}
          >
            {value}
          </span>
        </FormField>
      );
    }

    // 타입별로 적절한 컴포넌트 렌더링
    if (type === 'radio') {
      const value = watch(name) || '';
      return (
        <RadioField
          key={name}
          {...commonProps}
          value={value}
          options={options}
          onChange={(e) => {
            const newValue = e.target.value;
            const prevValue = watch(name); // setValue 전에 현재 값 저장
            setValue(name, newValue, { shouldValidate: false, shouldDirty: true });
            // 화면에서 전달한 onChange가 있으면 같이 호출 (setValue 이후)
            if (fieldOnChange) {
              fieldOnChange(e);
            }
            // radio 필드에서 값 선택 시 onFieldEnter 호출 (독립적으로 작동)
            if (fieldOnFieldEnter) {
              fieldOnFieldEnter(name, newValue, 'radio', 'select');
            }
            // onFormChange 호출 (독립적으로 작동, 둘 다 있으면 둘 다 호출됨)
            if (fieldOnFormChange) {
              fieldTriggerFormChange(name, prevValue, newValue);
            }
          }}
        />
      );
    }

    if (type === 'checkbox') {
      // options가 없거나 빈 배열이면 단일 체크박스 (문자열 "Y" 또는 "N"), 있으면 다중 체크박스 (문자열 "1,2,3")
      const isSingle = !options || options.length === 0;
      
      if (isSingle) {
        // 단일 체크박스: 문자열 "Y" 또는 "N"
        const value = watch(name) || 'N';
        return (
          <CheckboxField
            key={name}
            {...commonProps}
            value={value}
            options={options}
            onChange={(e) => {
              const newValue = e.target.value;
              const prevValue = watch(name); // setValue 전에 현재 값 저장
              setValue(name, newValue, { shouldValidate: false, shouldDirty: true });
              // 화면에서 전달한 onChange가 있으면 같이 호출 (setValue 이후)
              if (fieldOnChange) {
                fieldOnChange(e);
              }
              // 단일 체크박스에서 값 변경 시 onFieldEnter 호출 (독립적으로 작동)
              if (fieldOnFieldEnter) {
                fieldOnFieldEnter(name, newValue, 'checkbox', 'change');
              }
              // onFormChange 호출 (독립적으로 작동, 둘 다 있으면 둘 다 호출됨)
              if (fieldOnFormChange) {
                fieldTriggerFormChange(name, prevValue, newValue);
              }
            }}
          />
        );
      } else {
        // 다중 체크박스: 문자열을 배열로 변환하여 컴포넌트에 전달, onChange에서 다시 문자열로 변환
        const stringValue = watch(name) || '';
        const valueArray = stringValue ? stringValue.split(separator).filter(v => v) : [];
        
        return (
          <CheckboxField
            key={name}
            {...commonProps}
            value={valueArray}
            options={options}
            onChange={(e) => {
              // 배열을 구분자로 연결된 문자열로 변환
              const newStringValue = Array.isArray(e.target.value) && e.target.value.length > 0
                ? e.target.value.join(separator)
                : '';
              const prevValue = watch(name); // setValue 전에 현재 값 저장
              setValue(name, newStringValue, { shouldValidate: false, shouldDirty: true });
              // 화면에서 전달한 onChange가 있으면 같이 호출 (setValue 이후)
              if (fieldOnChange) {
                fieldOnChange(e);
              }
              // 다중 체크박스에서 값 선택 시 onFieldEnter 호출 (독립적으로 작동)
              if (fieldOnFieldEnter) {
                fieldOnFieldEnter(name, newStringValue, 'checkbox', 'select');
              }
              // onFormChange 호출 (독립적으로 작동, 둘 다 있으면 둘 다 호출됨)
              if (fieldOnFormChange) {
                fieldTriggerFormChange(name, prevValue, newStringValue);
              }
            }}
          />
        );
      }
    }

    if (type === 'select') {
      return (
        <SelectField
          key={name}
          {...commonProps}
          value={watch(name) || ''}
          options={options}
          onChange={(e) => {
            const newValue = e.target.value;
            const prevValue = watch(name); // setValue 전에 현재 값 저장
            setValue(name, newValue, { shouldValidate: false, shouldDirty: true });
            // 화면에서 전달한 onChange가 있으면 같이 호출 (setValue 이후)
            if (fieldOnChange) {
              fieldOnChange(e);
            }
            // select 필드에서 값 선택 시 onFieldEnter 호출 (독립적으로 작동)
            if (fieldOnFieldEnter) {
              fieldOnFieldEnter(name, newValue, 'select', 'select');
            }
            // onFormChange 호출 (독립적으로 작동, 둘 다 있으면 둘 다 호출됨)
            if (fieldOnFormChange) {
              fieldTriggerFormChange(name, prevValue, newValue);
            }
          }}
          min={min}
          max={max}
        />
      );
    }

    if (type === 'multiSelect') {
      // 문자열을 배열로 변환하여 컴포넌트에 전달, onChange에서 다시 문자열로 변환
      const stringValue = watch(name) || '';
      const valueArray = stringValue ? stringValue.split(separator).filter(v => v) : [];
      
      return (
        <MultiSelectField
          key={name}
          {...commonProps}
          value={valueArray}
          options={options}
          onChange={(e) => {
            // 배열을 구분자로 연결된 문자열로 변환
            const newStringValue = Array.isArray(e.target.value) && e.target.value.length > 0
              ? e.target.value.join(separator)
              : '';
            const prevValue = watch(name); // setValue 전에 현재 값 저장
            setValue(name, newStringValue, { shouldValidate: false, shouldDirty: true });
            // 화면에서 전달한 onChange가 있으면 같이 호출 (setValue 이후)
            if (fieldOnChange) {
              fieldOnChange(e);
            }
            // multiSelect 필드에서 값 선택 시 onFieldEnter 호출 (독립적으로 작동)
            if (fieldOnFieldEnter) {
              fieldOnFieldEnter(name, newStringValue, 'multiSelect', 'select');
            }
            // onFormChange 호출 (독립적으로 작동, 둘 다 있으면 둘 다 호출됨)
            if (fieldOnFormChange) {
              fieldTriggerFormChange(name, prevValue, newStringValue);
            }
          }}
        />
      );
    }

    if (type === 'date') {
      const isPairEnd = Boolean(pairStartField);
      const pairEndValue = pairWith ? watch(pairWith) || '' : '';
      const pairStartValue = pairStartField ? watch(pairStartField) || '' : '';
      return (
        <DateField
          key={name}
          {...commonProps}
          value={watch(name) || ''}
          min={min}
          max={max}
          showTodayShortcut={showTodayShortcut}
          showPresets={resolvedShowPresets}
          presetSet={presetSet}
          fiscalYear={fiscalYear}
          anchorFrom={anchorFrom}
          yearPreset={yearPreset}
          pairEndValue={pairEndValue}
          pairStartValue={pairStartValue}
          isPairEnd={isPairEnd}
          allowEmptyEnd={allowEmptyEnd}
          onPairInvalid={(reason) => showAlert(reason || '날짜 범위를 확인하세요.', 'warning')}
          onChange={(e) => {
            const newValue = e.target.value;
            const prevValue = watch(name); // setValue 전에 현재 값 저장
            setValue(name, newValue, { shouldValidate: false, shouldDirty: true });
            // 화면에서 전달한 onChange가 있으면 같이 호출 (setValue 이후)
            if (fieldOnChange) {
              fieldOnChange(e);
            }
            // date 필드에서 날짜 변경 시 onFieldEnter 호출 (독립적으로 작동)
            if (fieldOnFieldEnter) {
              fieldOnFieldEnter(name, newValue, 'date', 'change');
            }
            // onFormChange 호출 (독립적으로 작동, 둘 다 있으면 둘 다 호출됨)
            if (fieldOnFormChange) {
              fieldTriggerFormChange(name, prevValue, newValue);
            }
          }}
        />
      );
    }

    if (type === 'dateRange') {
      const startValue = startFieldName ? (watch(startFieldName) || '') : '';
      const endValue = endFieldName ? (watch(endFieldName) || '') : '';
      return (
        <DateRangeField
          key={name}
          {...commonProps}
          startFieldName={startFieldName}
          endFieldName={endFieldName}
          startValue={startValue}
          endValue={endValue}
          min={min}
          max={max}
          startLabel={startLabel}
          endLabel={endLabel}
          showTodayShortcut={showTodayShortcut}
          showPresets={resolvedShowPresets}
          presetSet={presetSet}
          fiscalYear={fiscalYear}
          anchorFrom={anchorFrom}
          yearPreset={yearPreset}
          onChange={(e) => {
            const fieldName = e.target.name;
            const newValue = e.target.value;
            const prevValue = watch(fieldName); // setValue 전에 현재 값 저장
            setValue(fieldName, newValue, { shouldValidate: false, shouldDirty: true });
            // 화면에서 전달한 onChange가 있으면 같이 호출 (setValue 이후)
            if (fieldOnChange) {
              fieldOnChange(e);
            }
            // dateRange 필드에서 날짜 변경 시 onFieldEnter 호출 (독립적으로 작동)
            if (fieldOnFieldEnter) {
              fieldOnFieldEnter(fieldName, newValue, 'dateRange', 'change');
            }
            // onFormChange 호출 (독립적으로 작동, 둘 다 있으면 둘 다 호출됨)
            if (fieldOnFormChange) {
              fieldTriggerFormChange(fieldName, prevValue, newValue);
            }
          }}
        />
      );
    }

    if (type === 'textarea') {
      // textarea에서 Enter 키 입력 시 onFieldEnter 호출을 위한 핸들러
      // textarea에서는 Enter가 줄바꿈이므로, Ctrl+Enter 또는 Shift+Enter 조합 사용
      const handleTextAreaKeyDown = (e) => {
        // 기존 onKeyDown이 있으면 먼저 실행
        if (onKeyDown) {
          onKeyDown(e);
        }
        // Ctrl+Enter 또는 Shift+Enter 입력 시 onFieldEnter 호출
        if ((e.ctrlKey || e.shiftKey) && e.key === 'Enter' && fieldOnFieldEnter) {
          const currentValue = e.target.value;
          fieldOnFieldEnter(name, currentValue, 'textarea', 'enter');
        }
      };

      return (
        <TextAreaField
          key={name}
          {...commonProps}
          value={watch(name) || ''}
          rows={rows || 4} // 기본값 4행
          onChange={(e) => {
            const newValue = e.target.value;
            const prevValue = watch(name); // setValue 전에 현재 값 저장
            setValue(name, newValue, { shouldValidate: false, shouldDirty: true });
            // onFormChange 호출 (독립적으로 작동)
            if (fieldOnFormChange) {
              fieldTriggerFormChange(name, prevValue, newValue);
            }
            // 화면에서 전달한 onChange가 있으면 같이 호출 (setValue 이후)
            if (fieldOnChange) {
              fieldOnChange(e);
            }
          }}
          onKeyDown={handleTextAreaKeyDown}
        />
      );
    }

    if (type === 'file') {
      const {
        accept,
        multiple = false,
      } = field;
      
      return (
        <FileField
          key={name}
          {...commonProps}
          value={watch(name) || null}
          accept={accept}
          multiple={multiple}
          onChange={(e) => {
            setValue(name, e.target.value, { shouldValidate: false });
            // 화면에서 전달한 onChange가 있으면 같이 호출 (setValue 이후)
            if (fieldOnChange) {
              fieldOnChange(e);
            }
          }}
        />
      );
    }

    // number 타입 처리
    if (type === 'number') {
      // number 필드에서 Enter 키 입력 시 onFieldEnter 호출을 위한 핸들러
      const handleNumberKeyDown = (e) => {
        // 기존 onKeyDown이 있으면 먼저 실행
        if (onKeyDown) {
          onKeyDown(e);
        }
        // Enter 키 입력 시 onFieldEnter 호출
        if (e.key === 'Enter' && fieldOnFieldEnter) {
          const currentValue = e.target.value;
          fieldOnFieldEnter(name, currentValue, 'number', 'enter');
        }
      };

      return (
        <NumberField
          key={name}
          {...commonProps}
          value={watch(name) ?? ''}
          min={min}
          max={max}
          step={step}
          roundMode={roundMode} // 반올림 모드 전달
          onChange={(e) => {
            const newValue = e.target.value;
            const prevValue = watch(name); // setValue 전에 현재 값 저장
            setValue(name, newValue, { shouldValidate: false, shouldDirty: true });
          // onFormChange 호출 (독립적으로 작동)
          if (fieldOnFormChange) {
            fieldTriggerFormChange(name, prevValue, newValue);
          }
          // 화면에서 전달한 onChange가 있으면 같이 호출 (setValue 이후)
          if (fieldOnChange) {
            fieldOnChange(e);
          }
        }}
        onKeyDown={handleNumberKeyDown}
        onKeyUp={onKeyUp}
      />
    );
  }

    // 회계연도 스피너 (▲/▼ · 휠 · ↑/↓)
    if (type === 'fiscalYear' || type === 'year') {
      const handleYearKeyDown = (e) => {
        if (onKeyDown) onKeyDown(e);
        if (e.key === 'Enter' && fieldOnFieldEnter) {
          fieldOnFieldEnter(name, e.target.value, 'fiscalYear', 'enter');
        }
      };

      return (
        <FiscalYearField
          key={name}
          {...commonProps}
          value={watch(name) ?? ''}
          min={min}
          max={max}
          onChange={(e) => {
            const newValue = e.target.value;
            const prevValue = watch(name);
            setValue(name, newValue, { shouldValidate: false, shouldDirty: true });
            if (fieldOnFormChange) {
              fieldTriggerFormChange(name, prevValue, newValue);
            }
            if (fieldOnChange) {
              fieldOnChange(e);
            }
            if (fieldOnFieldEnter) {
              fieldOnFieldEnter(name, newValue, 'fiscalYear', 'change');
            }
          }}
          onKeyDown={handleYearKeyDown}
          onKeyUp={onKeyUp}
        />
      );
    }

  // text 타입 및 기타 input 타입 (email 등)
  // Enter 키 입력 시 onFieldChange 호출을 위한 핸들러
  const handleKeyDown = (e) => {
    // 기존 onKeyDown이 있으면 먼저 실행
    if (onKeyDown) {
      onKeyDown(e);
    }
    // Enter 키 입력 시 onFieldEnter 호출 (독립적으로 작동)
    if (e.key === 'Enter' && fieldOnFieldEnter) {
      const currentValue = e.target.value;
      fieldOnFieldEnter(name, currentValue, type, 'enter');
    }
  };

  return (
    <TextField
      key={name}
      {...commonProps}
      type={type}
      value={watch(name) || ''}
      showSearch={showSearch}
      searchTitle={searchTitle}
      onSearch={
        showSearch
          ? (payload) => {
              if (typeof onSearch === 'function') onSearch(payload);
            }
          : undefined
      }
      onChange={(e) => {
        const newValue = e.target.value;
        const prevValue = watch(name); // setValue 전에 현재 값 저장
        setValue(name, newValue, { shouldValidate: false, shouldDirty: true });
        // onFormChange 호출 (독립적으로 작동)
        if (fieldOnFormChange) {
          fieldTriggerFormChange(name, prevValue, newValue);
        }
        // 화면에서 전달한 onChange가 있으면 같이 호출 (setValue 이후)
        if (fieldOnChange) {
          fieldOnChange(e);
        }
      }}
        onKeyDown={handleKeyDown}
        onKeyUp={onKeyUp}
      />
    );
  };

  // 행 단위로 그룹화하여 렌더링
  const AreaStyle = (compact || dense) ? FormAreaCompact : FormAreaStyle;
  const RowStyle = compact ? FormRowCompact : dense ? FormRowTight : FormRow;

  return (
    <AreaStyle>
      {fields.map((rowFields, rowIndex) => (
        <RowStyle key={rowIndex}>
          {rowFields.map((field) => renderField(field, onFieldEnter, triggerFormChange, onFormChange))}
        </RowStyle>
      ))}
    </AreaStyle>
  );
};

export default FormArea;

